
import React, { useRef, useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence, MotionConfig } from 'framer-motion';
import {
  Play, Pause, Volume2, VolumeX, ShieldCheck, Maximize, Minimize,
  Check, ArrowRight, ArrowDown, Heart, Award, GraduationCap, Sparkles, Instagram,
} from 'lucide-react';
import LeadCheckoutDialog, { CheckoutPlan } from '@/components/LeadCheckoutDialog';
import { captureFirstTouch, fetchGeo } from '@/lib/leadTracking';
import { AMAMENTACAO_DE_OURO, AMAMENTACAO_NA_PRATICA, DORES_E_LESOES } from '@/data/offers';

// Barra de progresso não-linear estilo Vturb (mais agressivo):
// 0–30% do vídeo → 0–70% visual (avança muito rápido)
// 30–100% do vídeo → 70–100% visual (avança devagar)
const getDisplayProgress = (currentTime: number, duration: number): number => {
  if (!duration || duration === 0) return 0;
  const real = currentTime / duration; // 0..1
  if (real <= 0.3) {
    return (real / 0.3) * 70;
  }
  return 70 + ((real - 0.3) / 0.7) * 30;
};

const formatTime = (secs: number): string => {
  const m = Math.floor(secs / 60);
  const s = Math.floor(secs % 60);
  return `${m}:${s.toString().padStart(2, '0')}`;
};

type Plan = CheckoutPlan & {
  tagline: string;
  description: string;
  benefits: string[];
  cta: string;
  featured?: boolean;
  badge?: string;
};

const PLANS: Plan[] = [
  {
    ...DORES_E_LESOES,
    tagline: 'Alívio imediato para dor ao amamentar',
    description: 'Técnicas rápidas para corrigir fissuras, posição e pega',
    benefits: ['Alívio para mamilos doloridos', 'Correções práticas de pega e postura', 'Método PPS aplicado direto na dor'],
    cta: 'Não quero sentir dor',
  },
  {
    ...AMAMENTACAO_NA_PRATICA,
    tagline: 'O método PPS para transformar sua amamentação',
    description: 'Aprenda na prática como amamentar com segurança e vínculo',
    benefits: ['Técnicas para pega, posição, confusão de bico, relactação e +', 'Passo a passo visual com exemplos reais', 'Mais segurança, menos improviso'],
    cta: 'Quero amamentar',
    featured: true,
    badge: 'Mais escolhido',
  },
  {
    ...AMAMENTACAO_DE_OURO,
    tagline: 'A experiência completa com certificado profissional',
    description: 'Para mães (e profissionais) que querem ir além',
    benefits: ['10 módulos + 14 aulas bônus', 'Certificado profissional incluso', 'Aulas práticas com mães reais'],
    cta: 'Quero dominar',
    badge: 'Completo',
  },
];

const RESULTS = [
  { id: '27-1', desc: '@starsoares, fala direto com quem está grávida agora' },
  { id: '27-2', desc: 'Avaliações no Google, inclui amamentar sem dor na 4ª filha' },
  { id: '27-3', desc: 'Cecília mamando após ajuste de posição e pega' },
  { id: '27-4', desc: 'Preparo antes do nascimento, zero fissura e mastite' },
  { id: '27-5', desc: 'Bianca Schorn, dermatologista' },
  { id: '27-6', desc: 'Isa, 3 anos e 4 meses amamentando' },
  { id: '27-8', desc: 'Mariane Oliveira, relato de aluna' },
  { id: '27-9', desc: 'Relato resolvido com consultoria remota' },
  { id: '27-11', desc: 'Meu curso é meu Google' },
];

const CREDENTIALS = [
  { icon: GraduationCap, title: 'Formação', desc: 'Nutricionista, Mestre em saúde da criança' },
  { icon: Award, title: 'Certificação', desc: 'Consultora Internacional de Amamentação (IBLCE)' },
  { icon: Sparkles, title: 'Experiência de 25 anos', desc: 'Pioneira na Consultoria de Amamentação' },
  { icon: Heart, title: 'Especialização', desc: 'Aleitamento Materno e Nutrição clínica' },
];

const scrollToPlans = () => document.getElementById('planos')?.scrollIntoView({ behavior: 'smooth' });

const VSL: React.FC = () => {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const maxReachedRef = useRef(0);
  const hoverTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const plansRef = useRef<HTMLElement>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [displayProgress, setDisplayProgress] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [isBuffering, setIsBuffering] = useState(false);
  const [showControls, setShowControls] = useState(false); // controles completos visíveis no hover
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState<CheckoutPlan | null>(null);
  const [showStickyCta, setShowStickyCta] = useState(false);

  // Top bar dynamic states
  const [locationStr, setLocationStr] = useState('sua região');
  const [randomCount, setRandomCount] = useState(0);

  useEffect(() => {
    captureFirstTouch();
    setRandomCount(Math.floor(Math.random() * (329 - 48 + 1)) + 48);
    fetchGeo().then((geo) => {
      if (geo.city) setLocationStr(geo.city);
    });
  }, []);

  // CTA fixo no mobile: aparece depois do vídeo e some quando os planos estão na tela
  useEffect(() => {
    let plansVisible = false;
    const update = () => setShowStickyCta(window.scrollY > 700 && !plansVisible);
    const observer = new IntersectionObserver(([entry]) => {
      plansVisible = entry.isIntersecting;
      update();
    }, { threshold: 0.15 });
    if (plansRef.current) observer.observe(plansRef.current);
    window.addEventListener('scroll', update, { passive: true });
    return () => {
      observer.disconnect();
      window.removeEventListener('scroll', update);
    };
  }, []);

  // ─── Fullscreen ──────────────────────────────────────────────────────────
  const toggleFullscreen = useCallback(() => {
    const el = containerRef.current;
    const video = videoRef.current;
    if (!el) return;

    // Helper: check if currently in fullscreen (cross-browser)
    const isInFullscreen = !!(document.fullscreenElement ||
      (document as any).webkitFullscreenElement ||
      (document as any).mozFullScreenElement);

    if (!isInFullscreen) {
      // Try standard API first
      if (el.requestFullscreen) {
        el.requestFullscreen().then(() => {
          // Lock to landscape when supported (Android Chrome, etc.)
          try {
            (screen.orientation as any).lock('landscape').catch(() => {});
          } catch (_) {}
        }).catch(() => {
          // Fallback for iOS Safari: fullscreen on the video element itself
          if (video && (video as any).webkitEnterFullscreen) {
            (video as any).webkitEnterFullscreen();
          }
        });
      } else if ((el as any).webkitRequestFullscreen) {
        // Webkit prefix (older Android / desktop Safari)
        (el as any).webkitRequestFullscreen();
        try {
          (screen.orientation as any).lock('landscape').catch(() => {});
        } catch (_) {}
      } else if (video && (video as any).webkitEnterFullscreen) {
        // Last resort: iOS Safari native video fullscreen
        (video as any).webkitEnterFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      } else if ((document as any).webkitExitFullscreen) {
        (document as any).webkitExitFullscreen();
      }
      // Release orientation lock
      try {
        (screen.orientation as any).unlock();
      } catch (_) {}
    }
  }, []);

  useEffect(() => {
    const onFsChange = () => {
      const inFs = !!(document.fullscreenElement ||
        (document as any).webkitFullscreenElement ||
        (document as any).mozFullScreenElement);
      setIsFullscreen(inFs);
    };
    // iOS fires webkitbeginfullscreen / webkitendfullscreen on the video element
    const onVideoWebkitBegin = () => setIsFullscreen(true);
    const onVideoWebkitEnd = () => setIsFullscreen(false);

    document.addEventListener('fullscreenchange', onFsChange);
    document.addEventListener('webkitfullscreenchange', onFsChange);
    document.addEventListener('mozfullscreenchange', onFsChange);
    videoRef.current?.addEventListener('webkitbeginfullscreen', onVideoWebkitBegin);
    videoRef.current?.addEventListener('webkitendfullscreen', onVideoWebkitEnd);
    return () => {
      document.removeEventListener('fullscreenchange', onFsChange);
      document.removeEventListener('webkitfullscreenchange', onFsChange);
      document.removeEventListener('mozfullscreenchange', onFsChange);
      videoRef.current?.removeEventListener('webkitbeginfullscreen', onVideoWebkitBegin);
      videoRef.current?.removeEventListener('webkitendfullscreen', onVideoWebkitEnd);
    };
  }, []);

  // ─── Hover: mostrar/ocultar controles (estilo Vturb) ────────────────────
  const handleMouseEnter = useCallback(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setShowControls(true);
  }, []);

  const handleMouseLeave = useCallback(() => {
    hoverTimeoutRef.current = setTimeout(() => setShowControls(false), 800);
  }, []);

  const handleMouseMove = useCallback(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setShowControls(true);
    hoverTimeoutRef.current = setTimeout(() => setShowControls(false), 2500);
  }, []);

  const handleTouchStart = useCallback(() => {
    if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    setShowControls(true);
    hoverTimeoutRef.current = setTimeout(() => setShowControls(false), 3000);
  }, []);

  // ─── Video event handlers ────────────────────────────────────────────────
  const handleTimeUpdate = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const cur = v.currentTime;
    const dur = v.duration || 0;

    // Anti-seek: se pular para frente, volta ao máximo atingido
    if (cur > maxReachedRef.current + 1.5) {
      v.currentTime = maxReachedRef.current;
      return;
    }
    if (cur > maxReachedRef.current) {
      maxReachedRef.current = cur;
    }

    setCurrentTime(cur);
    setDisplayProgress(getDisplayProgress(cur, dur));
  }, []);

  const handlePlay = useCallback(() => {
    setIsPlaying(true);
    setHasStarted(true);
    setIsBuffering(false);
  }, []);

  const handlePause = useCallback(() => setIsPlaying(false), []);
  const handleEnded = useCallback(() => setIsPlaying(false), []);
  const handleWaiting = useCallback(() => setIsBuffering(true), []);
  const handleCanPlay = useCallback(() => setIsBuffering(false), []);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) v.play();
    else v.pause();
  }, []);

  const toggleMute = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    v.muted = !v.muted;
    setIsMuted(v.muted);
  }, []);

  const handleVolumeChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const v = videoRef.current;
    if (!v) return;
    const val = parseFloat(e.target.value);
    v.volume = val;
    v.muted = val === 0;
    setVolume(val);
    setIsMuted(val === 0);
  }, []);

  // ─── Bloquear atalhos de teclado de seek ────────────────────────────────
  useEffect(() => {
    const block = (e: KeyboardEvent) => {
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA') return;
      if (['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(e.key)) {
        e.preventDefault();
      }
    };
    window.addEventListener('keydown', block);
    return () => window.removeEventListener('keydown', block);
  }, []);

  // ─── Cleanup ─────────────────────────────────────────────────────────────
  useEffect(() => {
    return () => {
      if (hoverTimeoutRef.current) clearTimeout(hoverTimeoutRef.current);
    };
  }, []);

  return (
    <MotionConfig reducedMotion="user">
    <div className="min-h-screen bg-[#faf7fc] font-sans text-gray-900 caret-[#8e3a87] selection:bg-[#e7a3df] selection:text-[#2a1744] [&_h1]:[text-wrap:balance] [&_h2]:[text-wrap:balance] [&_p]:[text-wrap:pretty] [&_dd]:[text-wrap:pretty]">

      {/* ── Top Bar Announcement ──────────────────────── */}
      <div className="bg-[#8e3a87] py-2 px-4 text-center text-white text-xs sm:text-sm font-medium">
        Mais de {randomCount} mães de {locationStr} descobriram o método
      </div>

      {/* ── Hero ──────────────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-gradient-to-b from-[#1f1035] via-[#2a1744] to-[#4a3371] px-4 pt-8 pb-44 sm:pt-12 sm:pb-56">
        <div className="pointer-events-none absolute -top-24 -right-24 h-96 w-96 rounded-full bg-[#c46bbb]/25 blur-3xl" />
        <div className="pointer-events-none absolute -bottom-32 -left-24 h-[28rem] w-[28rem] rounded-full bg-[#53c0c2]/15 blur-3xl" />

        {/* Borda curva entre o gradiente e o fundo claro */}
        <svg
          className="pointer-events-none absolute inset-x-0 bottom-0 h-16 w-full sm:h-28"
          viewBox="0 0 1440 120"
          preserveAspectRatio="none"
          aria-hidden="true"
        >
          <path fill="#faf7fc" d="M0,120 L0,70 C360,10 1080,10 1440,70 L1440,120 Z" />
        </svg>

        <div className="relative z-10 mx-auto max-w-4xl text-center">
          <motion.img
            initial={{ y: -8 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            src="/lovable-uploads/1de2e26f-4255-4f2e-8dc7-94f917c709eb.png"
            alt="Método PPS"
            className="mx-auto mb-8 h-10 opacity-90 brightness-0 invert sm:h-11"
          />

          <motion.h1
            initial={{ y: 12 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}
            className="mb-5 text-[30px] font-extrabold leading-[1.12] tracking-[-0.02em] text-white sm:text-4xl md:text-5xl"
          >
            Descubra como eu ajudei{' '}
            <span className="whitespace-nowrap text-[#f0b6e8]">+15 mil mães</span>{' '}
            a amamentarem de forma prazerosa
          </motion.h1>

          <motion.p
            initial={{ y: 12 }}
            animate={{ y: 0 }}
            transition={{ duration: 0.7, delay: 0.05, ease: [0.16, 1, 0.3, 1] }}
            className="mx-auto max-w-2xl text-base leading-relaxed text-white/80 sm:text-lg"
          >
            Enquanto mais de <strong className="text-white">80% das mães sofrem com dor e fissuras</strong> para amamentar, agora você descobrirá{' '}
            <strong className="text-[#e7a3df]">3 PASSOS SIMPLES</strong> para amamentar sem sofrer, vendo seu bebê crescendo forte e saudável só com o seu leite.
          </motion.p>
        </div>
      </section>

      {/* ── Player VSL ────────────────────────────────────────────────── */}
      <section className="px-4 pb-6">
        <div className="relative z-20 mx-auto -mt-36 max-w-4xl sm:-mt-48">

          {/* Moldura com brilho */}
          <div className="rounded-2xl bg-gradient-to-br from-[#e7a3df] via-[#c46bbb] to-[#5f448f] p-[3px] shadow-[0_30px_80px_rgba(42,23,68,0.45)] sm:rounded-3xl">
          {/* Container do vídeo */}
          <div
            ref={containerRef}
            className="group relative cursor-pointer overflow-hidden rounded-[14px] bg-black sm:rounded-[22px]"
            style={{ aspectRatio: '16/9' }}
            onContextMenu={(e) => e.preventDefault()}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onMouseMove={handleMouseMove}
            onTouchStart={hasStarted ? handleTouchStart : undefined}
            onClick={!hasStarted ? togglePlay : undefined}
          >
            <video
              ref={videoRef}
              className="w-full h-full object-cover"
              src="https://assets.cdn.filesafe.space/HCpY5BvpWwRE4MjT3zLF/media/66d38ce9f10da4f4dbd2afce.mov"
              poster="/lovable-uploads/ad697021-e3dd-433a-9eea-c5c708842c55.png"
              onTimeUpdate={handleTimeUpdate}
              onPlay={handlePlay}
              onPause={handlePause}
              onEnded={handleEnded}
              onWaiting={handleWaiting}
              onCanPlay={handleCanPlay}
              playsInline
              disablePictureInPicture
            />

            {/* Overlay de play quando ainda não iniciou */}
            {!hasStarted && (
              <div className="absolute inset-0 flex flex-col items-center justify-center bg-black/55 transition-colors duration-300 group-hover:bg-black/45">
                <div className="relative">
                  <span className="absolute inset-0 rounded-full bg-[#c46bbb]/50 motion-safe:animate-ping" />
                  <div className="relative flex h-20 w-20 items-center justify-center rounded-full border-4 border-white bg-[#a54d9e] shadow-[0_10px_30px_rgba(0,0,0,0.35)] transition-transform duration-300 group-hover:scale-110 sm:h-24 sm:w-24">
                    <Play className="ml-2 h-10 w-10 text-white sm:h-12 sm:w-12" fill="currentColor" />
                  </div>
                </div>
                <span className="mt-6 rounded-full bg-black/30 px-4 py-1.5 text-center text-xs font-semibold uppercase tracking-wide text-white/95 drop-shadow-lg backdrop-blur-sm sm:text-base">
                  Seu vídeo já começou, clique para ouvir
                </span>
              </div>
            )}

            {/* Spinner de buffering */}
            {isBuffering && hasStarted && (
              <div className="absolute inset-0 flex items-center justify-center bg-black/30 pointer-events-none">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#c46bbb]" />
              </div>
            )}

            {/* ── Barra de progresso + controles ────────────────────── */}
            {/* A barra fina rosa é SEMPRE visível */}
            {/* Os controles aparecem/somem no hover */}
            {hasStarted && (
              <div className="absolute bottom-0 left-0 right-0">

                {/* Controles — aparecem no hover, somem sem hover */}
                <div
                  className="transition-all duration-300 overflow-hidden"
                  style={{ maxHeight: showControls ? '56px' : '0px', opacity: showControls ? 1 : 0 }}
                >
                  <div className="bg-gradient-to-t from-black/85 to-transparent px-4 pt-6 pb-2 flex items-center gap-3">
                    {/* Play / Pause */}
                    <button
                      onClick={togglePlay}
                      className="text-white hover:text-[#c46bbb] transition-colors flex-shrink-0 focus:outline-none"
                      aria-label={isPlaying ? 'Pausar' : 'Reproduzir'}
                    >
                      {isPlaying ? (
                        <Pause className="w-6 h-6" fill="currentColor" />
                      ) : (
                        <Play className="w-6 h-6" fill="currentColor" />
                      )}
                    </button>

                    {/* Volume */}
                    <button
                      onClick={toggleMute}
                      className="text-white hover:text-[#c46bbb] transition-colors flex-shrink-0 focus:outline-none ml-2"
                      aria-label={isMuted ? 'Ativar som' : 'Silenciar'}
                    >
                      {isMuted || volume === 0 ? (
                        <VolumeX className="w-5 h-5" />
                      ) : (
                        <Volume2 className="w-5 h-5" />
                      )}
                    </button>

                    {/* Slider de volume */}
                    <input
                      type="range"
                      min={0}
                      max={1}
                      step={0.05}
                      value={isMuted ? 0 : volume}
                      onChange={handleVolumeChange}
                      className="w-16 h-1 accent-[#c46bbb] cursor-pointer"
                    />

                    {/* Tempo */}
                    <span className="text-white/80 text-xs font-mono ml-auto">
                      {formatTime(currentTime)}
                    </span>

                    {/* Fullscreen */}
                    <button
                      onClick={(e) => { e.stopPropagation(); toggleFullscreen(); }}
                      className="text-white hover:text-[#c46bbb] transition-colors flex-shrink-0 focus:outline-none"
                      aria-label={isFullscreen ? 'Sair da tela cheia' : 'Tela cheia'}
                    >
                      {isFullscreen ? (
                        <Minimize className="w-5 h-5" />
                      ) : (
                        <Maximize className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                </div>

                {/* Barra de progresso rosa — sempre visível, 6px */}
                <div
                  className="w-full cursor-not-allowed select-none"
                  style={{ height: showControls ? '8px' : '6px', transition: 'height 0.3s' }}
                  onMouseDown={(e) => e.preventDefault()}
                  onTouchStart={(e) => e.preventDefault()}
                >
                  {/* Track */}
                  <div className="w-full h-full bg-white/20">
                    {/* Fill rosa */}
                    <div
                      className="h-full transition-all duration-500 ease-linear"
                      style={{
                        width: `${displayProgress}%`,
                        backgroundColor: '#c46bbb',
                      }}
                    />
                  </div>
                </div>

              </div>
            )}
          </div>
          </div>

        </div>
      </section>

      {/* ── Planos ──────────────────────────────────────────────────────── */}
      <section id="planos" ref={plansRef} className="relative overflow-hidden bg-gradient-to-b from-[#faf7fc] via-[#f3e8f7] to-[#faf7fc] px-4 pt-6 pb-6 sm:pt-10 sm:pb-8">
        <div className="pointer-events-none absolute left-1/2 top-24 h-96 w-[48rem] -translate-x-1/2 rounded-full bg-[#c46bbb]/10 blur-3xl" />

        <div className="relative mx-auto max-w-6xl">
          <div className="mx-auto mb-12 max-w-2xl text-center sm:mb-16">
            <h2 className="text-3xl font-extrabold leading-tight text-[#2a1744] sm:text-4xl md:text-5xl">
              Escolha o curso ideal para a sua fase da jornada
            </h2>
            <p className="mt-4 text-base text-gray-600 sm:text-lg">
              Alívio imediato, técnica prática ou domínio completo da amamentação
            </p>
          </div>

          <div className="grid items-stretch gap-6 lg:grid-cols-3 lg:gap-5">
            {PLANS.map((plan, i) => {
              const featured = !!plan.featured;
              return (
                <motion.div
                  key={plan.id}
                  initial={{ y: 24 }}
                  whileInView={{ y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.06, ease: [0.16, 1, 0.3, 1] }}
                  className={`relative flex flex-col rounded-3xl p-7 sm:p-8 transition-transform duration-300 hover:-translate-y-1 ${
                    featured
                      ? 'order-first bg-gradient-to-b from-[#2a1744] to-[#4a3371] text-white shadow-[0_30px_70px_rgba(42,23,68,0.35)] ring-2 ring-[#c46bbb] lg:order-none lg:-my-4 lg:py-11'
                      : 'border border-[#5f448f]/10 bg-white shadow-xl shadow-[#5f448f]/5'
                  }`}
                >
                  {plan.badge && (
                    <span
                      className={`absolute -top-3.5 left-1/2 -translate-x-1/2 whitespace-nowrap rounded-full px-4 py-1 text-xs font-extrabold uppercase tracking-wider shadow-md ${
                        featured ? 'bg-[#f6d8f1] text-[#5c2457]' : 'bg-[#1f7f81] text-white'
                      }`}
                    >
                      {plan.badge}
                    </span>
                  )}

                  <h3 className={`text-2xl font-extrabold leading-tight ${featured ? 'text-white' : 'text-[#2a1744]'}`}>{plan.name}</h3>
                  <p className={`mt-2 font-semibold ${featured ? 'text-[#f0b6e8]' : 'text-[#8e3a87]'}`}>{plan.tagline}</p>
                  <p className={`mt-1 text-sm ${featured ? 'text-white/70' : 'text-gray-500'}`}>{plan.description}</p>

                  <div className={`my-6 border-y py-5 ${featured ? 'border-white/10' : 'border-gray-100'}`}>
                    <span className={`text-sm ${featured ? 'text-white/70' : 'text-gray-500'}`}>Investimento único</span>
                    <div className={`mt-1 text-4xl font-extrabold tracking-tight ${featured ? 'text-white' : 'text-[#2a1744]'}`}>{plan.price}</div>
                  </div>

                  <ul className="mb-8 flex-1 space-y-3">
                    {plan.benefits.map((benefit) => (
                      <li key={benefit} className="flex items-start gap-3">
                        <span className={`mt-0.5 flex h-5 w-5 flex-shrink-0 items-center justify-center rounded-full ${featured ? 'bg-[#c46bbb]' : 'bg-[#1f7f81]/10'}`}>
                          <Check className={`h-3.5 w-3.5 ${featured ? 'text-white' : 'text-[#1f7f81]'}`} strokeWidth={3} />
                        </span>
                        <span className={`text-[15px] leading-snug ${featured ? 'text-white/90' : 'text-gray-700'}`}>{benefit}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => setSelectedPlan(plan)}
                    className={`group flex w-full items-center justify-center gap-2 rounded-2xl px-6 py-4 text-base font-extrabold transition-[background-color,color,transform] duration-200 ease-out active:scale-[0.97] focus-visible:outline-none focus-visible:ring-4 sm:text-lg ${
                      featured
                        ? 'bg-[#a54d9e] text-white shadow-[0_8px_20px_rgba(15,5,30,0.35)] hover:bg-[#b456ad] focus-visible:ring-[#f0b6e8]/60'
                        : 'border-2 border-[#5f448f] text-[#5f448f] hover:bg-[#5f448f] hover:text-white focus-visible:ring-[#5f448f]/30'
                    }`}
                  >
                    {plan.cta}
                    <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
                  </button>
                </motion.div>
              );
            })}
          </div>

          <div className="mx-auto mt-12 flex max-w-2xl flex-col items-center gap-4 rounded-2xl border border-[#5f448f]/10 bg-white/80 px-6 py-5 text-center shadow-sm backdrop-blur sm:flex-row sm:text-left">
            <div className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-[#5f448f]">
              <ShieldCheck className="h-6 w-6 text-white" />
            </div>
            <div>
              <p className="font-bold text-[#2a1744]">Todos os cursos têm acesso vitalício e garantia de 7 dias.</p>
              <p className="text-sm text-gray-500">Aprenda no seu tempo, com segurança.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── Sobre a Rosane ───────────────────────────────────────────────── */}
      <section className="relative overflow-hidden bg-[#faf7fc] px-4 pt-8 pb-16 sm:pb-20">
        <div className="pointer-events-none absolute right-0 top-0 h-96 w-96 rounded-full bg-[#c46bbb]/10 blur-3xl" />
        <div className="pointer-events-none absolute -left-20 bottom-0 h-80 w-80 rounded-full bg-[#5f448f]/10 blur-3xl" />

        <div className="relative z-10 mx-auto max-w-5xl">
          <div className="mb-10 text-center">
            <h2 className="text-3xl font-extrabold text-[#2a1744] md:text-4xl">Rosane Baldissera</h2>
            <p className="mt-2 text-base font-medium text-[#8e3a87] sm:text-lg">Sua mentora no Método PPS</p>
          </div>

          <div className="flex flex-col items-center gap-10 md:flex-row md:items-center md:gap-14">
            {/* Foto */}
            <div className="flex flex-shrink-0 justify-center md:w-5/12">
              <div className="relative">
                <div className="absolute -inset-4 rounded-full bg-gradient-to-tr from-[#c46bbb] to-[#5f448f] opacity-20 blur-xl" />
                <img
                  src="/lovable-uploads/cf978e16-cc7f-4926-84e7-12e94ef564c1.png"
                  alt="Rosane Baldissera"
                  className="relative z-10 mx-auto w-72 drop-shadow-2xl md:w-full"
                  loading="lazy"
                />
              </div>
            </div>

            {/* Bio */}
            <div className="md:w-7/12">
              <p className="mb-8 max-w-[65ch] text-base leading-relaxed text-gray-600 md:text-[17px]">
                Sou Rosane Baldissera, <strong className="text-[#2a1744]">Pioneira na Consultoria de Amamentação no sul do Brasil</strong>,
                mãe do Felipe, amamentado até os 3 anos (exclusivamente até o 6° mês), Nutricionista (IPA – CRN 6721),
                Consultora Internacional de Amamentação certificada pelo <strong className="text-[#8e3a87]">IBLCE</strong> (Internacional Board
                Lactation Consultant Examiners), membro da <strong className="text-[#8e3a87]">ILCA</strong> (International Lactation Consultant
                Association), Mestre em saúde da criança e do adolescente (UFRGS), especialista em aleitamento materno e nutrição clínica (UGF),
                mas meu currículo só não é maior do que a minha paixão por ver mães e bebês felizes e saudáveis, desfrutando de uma
                amamentação prazerosa e sem sofrimento.
              </p>

              <dl className="grid grid-cols-1 gap-x-8 sm:grid-cols-2">
                {CREDENTIALS.map(({ icon: Icon, title, desc }) => (
                  <div key={title} className="flex items-start gap-3 border-t border-[#5f448f]/15 py-4">
                    <Icon className="mt-0.5 h-5 w-5 flex-shrink-0 text-[#8e3a87]" aria-hidden="true" />
                    <div>
                      <dt className="mb-0.5 text-sm font-bold text-[#5f448f]">{title}</dt>
                      <dd className="text-sm text-gray-600">{desc}</dd>
                    </div>
                  </div>
                ))}
              </dl>
            </div>
          </div>
        </div>
      </section>

      {/* ── Resultados ──────────────────────────────────────────────────── */}
      <section className="bg-white px-4 py-16 sm:py-24">
        <div className="mx-auto max-w-6xl">
          <div className="mb-12 text-center">
            <h2 className="mb-3 text-3xl font-extrabold text-[#2a1744] sm:text-4xl">Mães que já amamentam sem dor</h2>
            <p className="mx-auto max-w-xl text-base text-gray-600 sm:text-lg">
              Relatos reais de quem transformou a amamentação com o Método PPS
            </p>
          </div>

          <div className="columns-1 gap-5 sm:columns-2 lg:columns-3">
            {RESULTS.map((print) => (
              <figure
                key={print.id}
                className="mb-5 break-inside-avoid overflow-hidden rounded-2xl border border-gray-100 bg-white shadow-md transition-shadow duration-300 hover:shadow-xl"
              >
                <img
                  src={`/depoimentos-selecionados/${print.id}.jpg`}
                  alt={`Resultado Método PPS - ${print.desc}`}
                  className="h-auto w-full"
                  loading="lazy"
                />
                <figcaption className="flex items-center justify-between gap-2 border-t border-gray-100 px-4 py-3 text-xs font-medium text-gray-500">
                  <span>Mural de Resultados</span>
                  <Instagram className="h-3.5 w-3.5 flex-shrink-0 text-[#a54d9e]" />
                </figcaption>
              </figure>
            ))}
          </div>

          <div className="mt-12 text-center">
            <button
              onClick={scrollToPlans}
              className="inline-flex items-center gap-2 rounded-2xl bg-[#a54d9e] px-8 py-4 text-lg font-extrabold text-white shadow-[0_8px_20px_rgba(42,23,68,0.2)] transition-[background-color,transform] duration-200 ease-out hover:bg-[#8e3a87] active:scale-[0.97] focus-visible:outline-none focus-visible:ring-4 focus-visible:ring-[#a54d9e]/30"
            >
              Quero começar agora <ArrowRight className="h-5 w-5" />
            </button>
          </div>
        </div>
      </section>

      {/* ── Footer ──────────────────────────────────────────────────────── */}
      <footer className="bg-[#1f1035] px-4 py-8 pb-24 text-center text-xs text-white/50 sm:pb-8">
        <p>© {new Date().getFullYear()} Amamentação de Ouro · Método PPS · Rosane Baldissera</p>
        <a href="/termos-e-politicas" className="mt-2 inline-block underline-offset-2 hover:text-white/80 hover:underline">
          Termos e Políticas
        </a>
      </footer>

      {/* ── CTA fixo (mobile) ───────────────────────────────────────────── */}
      <AnimatePresence>
        {showStickyCta && !selectedPlan && (
          <motion.div
            initial={{ y: 100 }}
            animate={{ y: 0 }}
            exit={{ y: 100 }}
            className="fixed inset-x-0 bottom-0 z-40 border-t border-[#5f448f]/10 bg-white/95 p-3 backdrop-blur sm:hidden"
          >
            <button
              onClick={scrollToPlans}
              className="flex w-full items-center justify-center gap-2 rounded-xl bg-[#a54d9e] py-3.5 font-extrabold text-white shadow-lg active:scale-[0.97] transition-transform"
            >
              Ver os cursos a partir de R$ 97,90 <ArrowDown className="h-4 w-4" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>

      {/* ── Formulário → checkout ───────────────────────────────────────── */}
      <LeadCheckoutDialog plan={selectedPlan} onOpenChange={(open) => !open && setSelectedPlan(null)} />
    </div>
    </MotionConfig>
  );
};

export default VSL;
